import { useEffect } from 'react';
import { isEmpty } from 'lodash-es';
import type { FieldValues, UseFormReset } from 'react-hook-form';
import useFilterStore from './useFilterStore';
import useCurrentTasksFormStore from './useCurrentTasksFormStore';
import useTimetableStore from './useTimetableStore';
import type { GoogleRuleDetails } from '../google/types';
import { useCurrentNetwork } from '../../../hooks';
import type { RuleTypeMutation, RuleEntry } from '../types';
import type { Network } from '../../../types/shared';
import getFacebookInitialValues from '../facebook/utils/getFacebookInitialValues';
import getGoogleInitialValues from '../google/utils/getGoogleInitialValues';

const initialValues: Record<Network, (rule: any, mode: RuleTypeMutation) => FieldValues> = {
  facebook: getFacebookInitialValues,
  google: getGoogleInitialValues,
};

interface IProps {
  rule: RuleEntry | GoogleRuleDetails;
  reset: UseFormReset<FieldValues>;
  mode: RuleTypeMutation;
}

const useHydrateRuleData = ({ rule, reset, mode }: IProps) => {
  const network = useCurrentNetwork();
  const filterStore = useFilterStore();
  const taskStore = useCurrentTasksFormStore();
  const timetableStore = useTimetableStore();

  const getInitialValues = initialValues[network];

  useEffect(() => {
    reset(getInitialValues(rule, mode));

    if (isEmpty(filterStore.filterGroups)) {
      filterStore.setFilterGroups(rule.data.filters);
    }

    if (isEmpty(taskStore.tasksForm)) {
      taskStore.initialTasks(rule.data.tasks);
    }
    if (timetableStore.isEmpty) {
      if (rule.data.schedule_interval) {
        timetableStore.setInitialScheduleInterval(rule.data.schedule_interval);
      } else if (rule.data.timetable) {
        timetableStore.setInitialData(rule.data.timetable);
      }
    }

    return () => {
      filterStore.reset();
      taskStore.reset();
      timetableStore.reset();
      reset();
      window.history.replaceState({}, ''); // clear location state
    };
  }, [taskStore, getInitialValues, reset, rule, filterStore, timetableStore, mode]);
};

export default useHydrateRuleData;
